import { createModel, RematchDispatch } from '@rematch/core';
import produce from 'immer';

import { storeName as layoutModelStore } from './layout.model';
import { storeName as projectModelStore } from './project.model';
import { RootModel } from '../models';
import { storeName as floorEntityStore } from '../entities/floor.entity';
import { storeName as projectEntityStore } from '../entities/project.entity';
import { storeName as projectChildItemEntityStore } from '../entities/projectChildItem.entity';
import { storeName as projectItemEntityStore } from '../entities/projectItem.entity';
import { storeName as sitePlanPromotionsEntity } from '../entities/sitePlanPromotions.entity';
import {
  DEFAULT_SITE_PLAN_FILTER,
  FloorTypes,
  FocusItemTypes,
  ProjectItemTypes,
  ProjectTypes,
  SitePlanFilterDataTypes,
  SitePlanFilterTypes,
} from '../../types';
import {
  BEDROOM_TYPE,
  PROJECT_ITEM_TYPE,
  CONTENT_VISUALIZATION_MODE,
  DEFAULT_CONTENT_PANEL_DISPLAY_ITEM,
  SITE_PLAN_DETAIL_PAGE_LINK,
  SITE_PLAN_DETAIL_EXTENDABLE_PAGE_LINK,
  MODULE_PAGE_LINK,
  SITEPLAN_LANGUAGE_PATH,
  LanguageTypes,
} from '../../commons/constants';
import { getSitePlanFilterData } from '../../services/projectChildItem.service';
import { FloorLayoutTypes, VillaBlockLayoutTypes } from '../../types/layout';
import { convertSitePlanFilterResponse } from '../../utils/convertUtils/sitePlanFilter';

export const storeName = 'sitePlanModel';

type FilterDataTypes = {
  direction: string[];
  floor: SitePlanFilterDataTypes[];
  itemCode: SitePlanFilterDataTypes[];
  square: number[];
  bedroom: number[];
};

type SitePlanModelTypes = {
  projectItemId: number; // selected project item
  projectItemType: string;
  projectItemCode: string;
  projectItemKey: string;
  focusProjectItem: FocusItemTypes;
  floorId: number; // selected floor
  floorCode: string;
  floorKey: string;
  projectChildItemId: number; // selected project child item
  hoverProjectChildItemId: number;
  focusProjectChildItem: FocusItemTypes;
  defaultFilterData: SitePlanFilterTypes;
  filterData: SitePlanFilterTypes;
  isShowSlider: boolean;
  queryKey: { projectItem?: string; floor?: string; childItem?: string };
  mobileContentState: { isInitial: boolean; isShowFilterContent: boolean };
  detailInformationCoords: { x: number; y: number };
  display: string;
  visualizationType: string;
  visualizationViewMapType: string;
  numberOfDisplayItem: number; // number of content panel display items
  showPopUpBreadcrumb: boolean; // show popup Breadcrumb check mode webrotate
};

const initialState: SitePlanModelTypes = {
  projectItemId: null,
  projectItemType: null,
  projectItemCode: null,
  projectItemKey: null,
  focusProjectItem: null,
  floorId: null,
  floorCode: null,
  floorKey: null,
  projectChildItemId: null,
  hoverProjectChildItemId: null,
  focusProjectChildItem: null,
  defaultFilterData: { ...DEFAULT_SITE_PLAN_FILTER },
  filterData: { ...DEFAULT_SITE_PLAN_FILTER },
  isShowSlider: true,
  queryKey: {},
  mobileContentState: { isInitial: false, isShowFilterContent: false },
  detailInformationCoords: null,
  display: null,
  visualizationType: CONTENT_VISUALIZATION_MODE.MODE_2D.key,
  visualizationViewMapType: CONTENT_VISUALIZATION_MODE.MODE_2D.key,
  numberOfDisplayItem: DEFAULT_CONTENT_PANEL_DISPLAY_ITEM,
  showPopUpBreadcrumb: false,
};

export const sitePlanModel: any = createModel<RootModel>()({
  name: storeName,
  state: initialState,
  reducers: {
    setModeVisualizationType: produce((state, payload: string) => {
      state.visualizationType = payload;
    }),
    setModeVisualizationViewMapType: produce((state, payload: string) => {
      state.visualizationViewMapType = payload;
    }),
    setIsShowSlider: produce((state, payload: boolean) => {
      state.isShowSlider = payload;
    }),
    setProjectItem: produce(
      (state, payload: { id: number; type: string; code: string; key: string }) => {
        if (!payload) {
          state.projectItemId = initialState.projectItemId;
          state.projectItemType = initialState.projectItemType;
          state.projectItemCode = initialState.projectItemCode;
          state.projectItemKey = initialState.projectItemKey;
          state.floorId = initialState.floorId;
          state.floorCode = initialState.floorCode;
          state.floorKey = initialState.floorKey;
          state.projectChildItemId = initialState.projectChildItemId;
          state.hoverProjectChildItemId = initialState.hoverProjectChildItemId;
          state.focusProjectChildItem = initialState.focusProjectChildItem;
          state.isShowSlider = initialState.isShowSlider;
          return;
        }

        state.projectItemId = payload.id;
        state.projectItemType = payload.type;
        state.projectItemCode = payload.code;
        state.projectItemKey = payload.key;
      }
    ),
    setFocusProjectItem: produce((state, payload: FocusItemTypes) => {
      if (!payload && state.focusProjectItem && state.focusProjectItem.isShowModal) return;

      state.focusProjectItem = payload || initialState.focusProjectItem;
      state.isShowSlider = initialState.isShowSlider;
    }),
    resetFocusProjectItem: produce((state) => {
      state.focusProjectItem = initialState.focusProjectItem;
    }),
    setFloor: produce((state, payload: { id: number; code: string; key: string }) => {
      if (payload && payload.key === state.floorKey) return;
      if (!payload) {
        state.floorId = initialState.floorId;
        state.floorCode = initialState.floorCode;
        state.floorKey = initialState.floorKey;
        return;
      }

      state.floorId = payload.id;
      state.floorCode = payload.code;
      state.floorKey = payload.key;
      state.isShowSlider = initialState.isShowSlider;
      // reset child state
      state.projectChildItemId = initialState.projectChildItemId;
    }),
    setProjectChildItemId: produce((state, payload: number) => {
      state.projectChildItemId = payload || initialState.projectChildItemId;
    }),
    setHoverProjectChildItemId: produce((state, payload: number) => {
      state.hoverProjectChildItemId = payload || initialState.hoverProjectChildItemId;
    }),
    setFocusProjectChildItem: produce((state, payload: FocusItemTypes) => {
      if (!payload) {
        state.focusProjectChildItem = initialState.focusProjectChildItem;
        return;
      }

      state.focusProjectChildItem = payload;
    }),
    setDefaultFilterData: produce((state, payload: FilterDataTypes) => {
      state.defaultFilterData = payload;
    }),
    setFilterData: produce((state, payload: FilterDataTypes) => {
      state.filterData = payload;
    }),
    setQueryKey: produce((state, payload) => {
      state.queryKey = payload;
    }),
    setMobileContentState: produce((state, payload) => {
      const {
        isInitial = state.mobileContentState.isInitial,
        isShowFilterContent = state.mobileContentState.isShowFilterContent,
      } = payload;

      state.mobileContentState.isInitial = isInitial;
      state.mobileContentState.isShowFilterContent = isShowFilterContent;
    }),
    setDetailInformationCoords: produce((state, payload) => {
      state.detailInformationCoords = payload;
    }),
    setDisplay: produce((state, payload) => {
      state.display = payload;
    }),
    setConfig: produce((state, payload: { numberOfDisplayItem?: number }) => {
      if (payload.numberOfDisplayItem) state.numberOfDisplayItem = payload.numberOfDisplayItem;
    }),
    setShowPopUpBreadcrumb: produce((state, payload: boolean) => {
      state.showPopUpBreadcrumb = payload;
    }),
  },
  effects: (dispatch: RematchDispatch<any>) => ({
    loadPromotions: dispatch[sitePlanPromotionsEntity].loadPromotions,
    async getDefaultFilter(_, state) {
      const projectItem = state[storeName].projectItemId;
      let projectId;

      if (!projectItem) {
        projectId = state[projectModelStore].projectId;
      }

      // request API for get default top bar filter Data
      const result = await getSitePlanFilterData({ projectItemId: projectItem, projectId });

      if (result) {
        const filterData: SitePlanFilterTypes = convertSitePlanFilterResponse(result);
        dispatch[storeName].setDefaultFilterData({
          ...filterData,
        });
      } else {
        dispatch[storeName].setDefaultFilterData({ ...DEFAULT_SITE_PLAN_FILTER });
      }
    },
    updateFilter(payload: SitePlanFilterTypes, state) {
      const selectedProjectItem = state[storeName].projectItemId;
      let { projectItem } = payload;
      if (selectedProjectItem) {
        projectItem = [selectedProjectItem];
      }
      this.setFilterData({ ...state[storeName].filterData, ...payload, projectItem });
    },
    getDataByFilter(_, state) {
      let filter = state[storeName].filterData;
      // check and convert bedroom data
      if (filter.bedroom.length) {
        const bedroom = filter.bedroom.map((i: string) => {
          const item = Object.values(BEDROOM_TYPE).find((b) => b.key === i);
          if (item) return item.count ? item.count : item.key;

          return i;
        });

        filter = { ...filter, bedroom };
      }

      if (!filter.projectItem.length) {
        const projectItem = state[storeName].projectItemId;
        if (projectItem) {
          filter = {
            ...filter,
            projectItem: [projectItem],
          };
        }
      }

      dispatch[projectChildItemEntityStore].filter({
        ...filter,
      });
    },
    updateFilterAndGetData(payload: SitePlanFilterTypes, state) {
      this.updateFilter(payload);
      this.getDataByFilter();
    },
    getDataByKeyword(payload: string) {
      dispatch[projectChildItemEntityStore].filter({ keyword: payload });
    },
    updateFocusProjectItem(id, state) {
      if (
        !id ||
        (state[storeName].focusProjectItem && state[storeName].focusProjectItem.id === id)
      ) {
        return;
      }

      const project: ProjectTypes =
        state[projectEntityStore].ids[state[projectModelStore].projectId];
      const projectItemLayout = project.layoutItems.find((i) => i.projectItemId === id);

      if (projectItemLayout) {
        this.setFocusProjectItem({
          id,
          coords: projectItemLayout.zoneCoords,
          labelCoords: projectItemLayout.labelCoords,
        });
      } else {
        this.setFocusProjectItem();
      }
    },
    setFocusProjectItemShowModal(id, state) {
      // not do anything when current device is mobile device
      if (state[layoutModelStore].isMobileDevice) return;

      if (!id) {
        // only check case show modal info
        dispatch[storeName].resetFocusProjectItem();
      } else if (
        state[storeName].focusProjectItem &&
        state[storeName].focusProjectItem.id === +id
      ) {
        dispatch[storeName].setFocusProjectItem({
          ...state[storeName].focusProjectItem,
          isShowModal: true,
        });
      } else {
        dispatch[storeName].setFocusProjectItem({
          id,
          isShowModal: true,
        });
      }
    },
    async checkLoadProjectItemByKey(key, state) {
      // check load project item by key after redirect from detail to detail extendable
      if (state[storeName].projectItemKey === key) {
        return new Promise((resolve) => {
          resolve(state[projectItemEntityStore].ids[state[storeName].projectItemId]);
        });
      }

      return dispatch[projectItemEntityStore].loadByKey(key);
    },
    setRedirectDetailData(payload, state) {
      const {
        history,
        projectKey = state[projectModelStore].projectKey,
        itemKey = state[storeName].projectItemKey,
        itemType = state[storeName].projectItemType,
        floorKey = state[storeName].floorKey,
        childItemKey,
      } = payload;
      const language: LanguageTypes = state[layoutModelStore].locale;

      if (payload.itemKey) {
        dispatch[storeName].setFloor();
        dispatch[storeName].setProjectChildItemId();
      }

      let link: string;
      switch (itemType) {
        case PROJECT_ITEM_TYPE.BUILDING:
          if (!floorKey) {
            link = dispatch[projectModelStore]
              .getHomePageLink({ link: SITE_PLAN_DETAIL_PAGE_LINK })
              .replace(':languagePath', SITEPLAN_LANGUAGE_PATH[language.locale.toUpperCase()])
              .replace(':projectKey', projectKey)
              .replace(':itemKey', itemKey);
          } else {
            let extendable = floorKey;
            if (childItemKey) extendable = `${extendable}--${childItemKey}`;

            link = dispatch[projectModelStore]
              .getHomePageLink({ link: SITE_PLAN_DETAIL_EXTENDABLE_PAGE_LINK })
              .replace(':languagePath', SITEPLAN_LANGUAGE_PATH[language.locale.toUpperCase()])
              .replace(':projectKey', projectKey)
              .replace(':itemKey', itemKey)
              .replace(':extendable', extendable);
          }
          break;
        case PROJECT_ITEM_TYPE.VILLA_BLOCK:
          let extendable = '-1';
          if (childItemKey) {
            extendable = `-1--${childItemKey}`;
          }

          link = dispatch[projectModelStore]
            .getHomePageLink({ link: SITE_PLAN_DETAIL_EXTENDABLE_PAGE_LINK })
            .replace(':languagePath', SITEPLAN_LANGUAGE_PATH[language.locale.toUpperCase()])
            .replace(':projectKey', projectKey)
            .replace(':itemKey', itemKey)
            .replace(':extendable', extendable);
          break;

        default:
          break;
      }

      if (history && !!link && history.location.pathname !== link) {
        history.push(link);
      }
    },

    // S01-2800 update pan zoom when focus project child item
    updateFocusProjectChildItem(id, state) {
      if (
        !id ||
        (state[storeName].focusProjectChildItem && state[storeName].focusProjectChildItem.id === id)
      ) {
        return;
      }

      const projectItem: ProjectItemTypes =
        state[projectItemEntityStore].ids[state[storeName].projectItemId];

      let childItemLayout: FloorLayoutTypes | VillaBlockLayoutTypes;
      switch (projectItem.type) {
        case PROJECT_ITEM_TYPE.BUILDING:
          const floor: FloorTypes = state[floorEntityStore].ids[state[storeName].floorId];
          if (floor && floor.layoutItems) {
            childItemLayout = floor.layoutItems.find((i) => i.projectChildItemId === id);
          }
          break;

        case PROJECT_ITEM_TYPE.VILLA_BLOCK:
          if (projectItem.layoutItems) {
            childItemLayout = projectItem.layoutItems.find((i: any) => i.projectChildItemId === id);
          }
          break;

        default:
          break;
      }

      if (childItemLayout) {
        this.setFocusProjectChildItem({
          id,
          coords: childItemLayout.zoneCoords,
          labelCoords: childItemLayout.labelCoords,
        });
      } else {
        this.setFocusProjectChildItem();
      }
    },
    goToSitePlanPage(history, state) {
      const language: LanguageTypes = state[layoutModelStore].locale;

      this.setProjectItem();
      this.setMobileContentState({ isInitial: false, isShowFilterContent: false });

      const link = dispatch[projectModelStore]
        .getHomePageLink({ link: MODULE_PAGE_LINK })
        .replace(':projectKey', state[projectModelStore].projectKey)
        .replace(':activeKey', SITEPLAN_LANGUAGE_PATH[language.locale.toUpperCase()]);

      if (history && !!link) history.push(link);
    },
  }),
});

export default sitePlanModel;
